import React, { useState, useEffect, useMemo } from 'react';
import { DOCUMENT_TYPES, ICONS } from 'src/common/constants';
import Button from '../Button';
import Col from '../Col';
import File from '../File';
import Form, { Heading } from '../Form';
import FontAwesomeIcon from '../Icon';
import Input from '../Input';
import Row from '../Row';
import Textarea from '../Textarea';
import Tile from '../Tile';
import Toast from '../Toast';
import Loader from '../Loader';
import { clearValidation } from 'src/store/actions/validation';
import { selectValidation } from 'src/store/selectors/Validation';
import { toast } from 'react-toastify';
import CheckBox from '../Checkbox';
import { variables } from 'src/Tenants';
import { onUrlImageExtensionExist } from 'src/helpers/CommonHelpers';
import { shouldBeAn, titleCase } from 'src/common/textUtils';
import { useSelector, useDispatch } from 'react-redux';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import 'react-toastify/dist/ReactToastify.css';
import './document-form.scss';
import { useDocumentUpload } from 'src/pages/SharedPageCustomHooks/UseDocumentUpload/useDocumentUpload';

interface UserRole {
  role: {
    name: string;
  };
}

// Define TypeScript interfaces for our props and state
interface DocumentFormProps {
  onClose: () => void;
  breadcrumb?: string;
  onSaveDocument: (formData: DocumentFormData) => void;
  from?: string;
  user: {
    roles: UserRole[];
  };
}

interface ClearButtonProps {
  onClick: () => void;
  title?: string;
}

// Validates if the input string is a valid YouTube iframe embed code.
export const isValidYouTubeEmbedCode = (input: string): boolean => {
  if (!input) return true; // Empty input is considered valid.

  // This regex checks for a properly formatted YouTube iframe embed code- it looks for an iframe tag with a src attribute pointing to youtube.com/embed.
  const iframeEmbedRegex: RegExp =
    /<iframe[^>]*src=["']https?:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/[a-zA-Z0-9_-]{11}[^"']*["'][^>]*><\/iframe>/i;

  return iframeEmbedRegex.test(input);
};

export const isValidVimeoEmbedCode = (input: string): boolean => {
  if (!input) return true; // Empty input is considered valid.

  // This regex checks for a properly formatted Vimeo iframe embed code
  const vimeoEmbedRegex: RegExp =
    /<iframe[^>]*src=["']https?:\/\/(?:player\.)?vimeo\.com\/video\/\d+[^"']*["'][^>]*><\/iframe>/i;

  return vimeoEmbedRegex.test(input);
};

const ClearButton = ({ onClick, title = 'Clear' }: ClearButtonProps) => (
  <span
    onClick={onClick}
    style={{
      color: 'red',
      marginLeft: '10px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
    }}
    title={title}
  >
    ✕
  </span>
);

// Main DocumentForm component
const DocumentForm = ({
  onClose,
  breadcrumb,
  onSaveDocument,
  from,
  user,
}: DocumentFormProps) => {
  const dispatch = useDispatch();
  const loader = useSelector(selectFormLoader);
  const validation = useSelector(selectValidation);

  // Component state.
  const [buttonPressed, setButtonPressed] = useState<string>('');
  const [documentTypes, setDocumentTypes] = useState<string[]>([]);
  const [formData, setFormData] = useState<DocumentFormData>({
    docAvailability: false,
    isImportantDocument: false,
  });

  const updateFormData = (data: Partial<DocumentFormData>) => {
    setFormData((prevData: DocumentFormData) => ({
      ...prevData,
      ...data,
    }));
  };

  const { errorMessage, validateAndProcessFile, clearError } =
    useDocumentUpload({
      documentType: formData.type,
      maxSizeBytes: 25 * 1024 * 1024,
      onSuccess: (file, uploadType) => {
        updateFormData({
          pendingFile: file,
          uploadType,
          Embed: null,
        });
      },
    });

  const clearFormData = () => {
    setFormData({
      docAvailability: false,
      isImportantDocument:
        from === 'Service Request' || from === 'Contact' ? undefined : false,
    });
  };

  const dispatchClearValidation = () => {
    dispatch(clearValidation());
  };

  const saveDoc = () => {
    onSaveDocument(formData);
  };

  const onSelectDocument = async (file: any) => {
    if (!(file && file.length > 0)) {
      return;
    }

    const selectedFile = file[0];
    validateAndProcessFile(selectedFile);
  };

  const onSelectDocumentType = (documentType: string) => {
    updateFormData({ type: documentType });
    clearError();
  };

  // Function to handle embed changes and clear image if needed
  const handleEmbedChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const embedValue: string = e.target.value;
    // If adding embed text, clear any uploaded images.
    if (embedValue && formData.image) {
      updateFormData({
        Embed: embedValue,
        image: null,
        uploadType: null,
      });
    } else {
      updateFormData({ Embed: embedValue });
    }
  };

  const removeAttachedFileFormData = () => {
    updateFormData({ pendingFile: null, uploadType: null });
  };

  // Determine if the embed field should be disabled.
  const isEmbedFieldDisabled: boolean =
    formData.uploadType === 'Doc' || formData.uploadType === 'Image';

  // Determine if the file upload should be disabled.
  const isFileUploadDisabled: boolean = Boolean(
    formData.Embed && formData.Embed.trim().length > 0
  );

  // Convert render method to return statement.
  const headline = `Add ${shouldBeAn(formData.type || 'Document')} ${titleCase(
    formData.type || 'Document'
  )}`;

  // Validate YouTube embed code
  const isYoutubeEmbedValid: boolean = useMemo(() => {
    return isValidYouTubeEmbedCode(formData.Embed || '');
  }, [formData.Embed]);

  const isVimeoEmbedValid: boolean = useMemo(() => {
    return isValidVimeoEmbedCode(formData.Embed || '');
  }, [formData.Embed]);

  const isEmbedValid: boolean = useMemo(() => {
    return isYoutubeEmbedValid || isVimeoEmbedValid;
  }, [isYoutubeEmbedValid, isVimeoEmbedValid]);

  // Check if the form should be disabled based on embed validity.
  const isFormDisabled: boolean = useMemo(() => {
    if (!formData.type) {
      return true;
    }

    const hasEmbed: boolean = Boolean(
      formData.Embed && formData.Embed.trim().length > 0
    );

    // Disable if there's embed content and it's invalid.
    const isEmbedInvalid: boolean = hasEmbed && !isYoutubeEmbedValid;

    // Check for file presence - either pendingFile or existing image URL.
    const hasFile: boolean = Boolean(formData.pendingFile || formData.image);

    // Check if we have valid content (either a file OR a valid embed).
    const hasValidContent: boolean =
      hasFile || (hasEmbed && (isYoutubeEmbedValid || isVimeoEmbedValid));

    // For Service Request forms, we only need valid content.
    if (from === 'Service Request') {
      return !hasValidContent || isEmbedInvalid;
    }

    // For Video type with valid embed, don't require name/description.
    if (
      formData.type === 'Video' &&
      hasEmbed &&
      (isYoutubeEmbedValid || isVimeoEmbedValid)
    ) {
      return false;
    }

    // For other form types or non-embed Video uploads, check name and description
    return !hasValidContent || isEmbedInvalid;
  }, [formData, isYoutubeEmbedValid, from]);

  useEffect(() => {
    removeAttachedFileFormData();
  }, [formData.type]);

  // Initial setup - replaces componentDidMount.
  useEffect(() => {
    updateFormData({ docAvailability: false });

    if (!(from === 'Service Request' || from === 'Contact')) {
      updateFormData({ isImportantDocument: false });
    }

    if (
      from === 'Service Request' &&
      user.roles[0].role.name === 'Home Owner'
    ) {
      setDocumentTypes(['Image']);
      onSelectDocumentType('Image');
    } else {
      setDocumentTypes(DOCUMENT_TYPES);
    }
  }, [from, user.roles]);

  // Handle validation - replaces componentDidUpdate for validation changes.
  useEffect(() => {
    if (!validation.status) return;

    if (
      validation.message === 'FaqDocNotes' &&
      buttonPressed === 'SaveAndClose'
    ) {
      onClose();
      clearFormData();
      return dispatchClearValidation();
    }

    if (
      validation.message === 'FaqDocNotes' &&
      buttonPressed === 'SaveAndAdd'
    ) {
      clearFormData();
      dispatchClearValidation();

      if (!(from === 'Service Request' || from === 'Contact')) {
        updateFormData({ isImportantDocument: false });
      }
    }
  }, [validation, buttonPressed, from]);

  return (
    <Form
      headline={headline}
      onClose={onClose}
      onSubmit={saveDoc}
      actions={
        <>
          {loader ? <Loader center={true} /> : null}
          <Button
            color="semantic"
            size="small"
            label="Save and Add Another"
            onClick={() => setButtonPressed('SaveAndAdd')}
            disabled={isFormDisabled}
          />
          <Button
            color="teal"
            size="small"
            label="Save Document"
            onClick={() => setButtonPressed('SaveAndClose')}
            disabled={isFormDisabled}
          />
        </>
      }
      component={
        <>
          <Row chonky>
            {documentTypes.length > 1 && (
              <Col chonky xs="12" md="6">
                {breadcrumb && (
                  <>
                    <Toast
                      message={`You are adding a document to ${breadcrumb}.`}
                      actions={undefined}
                      color={undefined}
                    />
                    <br />
                    <br />
                  </>
                )}
                {/* Show document type selection tooltip if no type is selected. */}
                {!formData.type && (
                  <div className="document-type-container">
                    <div className="document-type-warning">
                      <FontAwesomeIcon
                        glyph="exclamation-circle"
                        weight="light"
                        size="small"
                      />
                      <span>Please select a document type to continue</span>
                    </div>
                    <br />
                  </div>
                )}
                <Heading>Document Type</Heading>
                <Row granola>
                  {documentTypes.map((documentType: string, i: number) => (
                    <Col granola key={i} md="4" xs="6">
                      <Tile
                        icon={
                          <FontAwesomeIcon
                            weight="light"
                            glyph={
                              ICONS[
                                documentType === 'General'
                                  ? 'OtherDoc'
                                  : documentType
                              ]
                            }
                          />
                        }
                        onClick={() => onSelectDocumentType(documentType)}
                        active={documentType === formData.type}
                        title={documentType}
                        size="small"
                      />
                    </Col>
                  ))}
                </Row>
              </Col>
            )}
            <Col chonky xs="12" md={documentTypes.length > 1 ? '6' : '12'}>
              <File
                label={
                  formData.type === 'Video'
                    ? 'Document'
                    : formData.type || 'Document'
                }
                documentType={formData.type}
                onSelect={(file: any) => onSelectDocument(file)}
                files={
                  formData.uploadType === 'Doc'
                    ? []
                    : formData.image
                      ? formData.image
                      : []
                }
                image={null}
                errors={errorMessage}
                required={true}
                disabled={isFileUploadDisabled || !formData.type}
              />
              {isFileUploadDisabled && (
                <div
                  style={{
                    color: 'red',
                    marginBottom: '10px',
                    fontSize: '14px',
                  }}
                >
                  Remove embed code to upload video file
                </div>
              )}
              {/* Display the file preview after it's attached and the option to clear it out. */}
              {formData.pendingFile ? (
                <div style={{ marginBottom: '1rem' }}>
                  <label>
                    {formData.pendingFile.name} (
                    {(formData.pendingFile.size / 1024).toFixed(2)} KB)
                  </label>
                  <ClearButton
                    onClick={() => removeAttachedFileFormData()}
                    title="Remove document"
                  />
                </div>
              ) : null}
              {formData.type === 'Video' ? (
                <div>
                  <div
                    style={{
                      textAlign: 'center',
                      color: '#43467F',
                      fontWeight: 700,
                    }}
                  >
                    {' '}
                    (OR)
                  </div>
                  <Textarea
                    value={formData.Embed}
                    onChange={handleEmbedChange}
                    label="Embed "
                    block
                    type="media"
                    disabled={isEmbedFieldDisabled}
                  />
                  {isEmbedFieldDisabled && (
                    <div
                      style={{
                        marginBottom: '5px',
                        fontSize: '14px',
                        color: 'red',
                      }}
                    >
                      Remove uploaded document to use embed code
                    </div>
                  )}
                  {formData.Embed && !isEmbedValid && (
                    <div
                      style={{
                        color: 'red',
                        marginBottom: '5px',
                        fontSize: '14px',
                      }}
                    >
                      {
                        'Please enter a valid YouTube or Vimeo iframe embed code'
                      }
                    </div>
                  )}
                  {formData.Embed && isYoutubeEmbedValid && (
                    <div
                      style={{
                        color: 'green',
                        marginBottom: '5px',
                        fontSize: '14px',
                      }}
                    >
                      {'This is a valid embedded YouTube video'}
                    </div>
                  )}
                  {formData.Embed && isVimeoEmbedValid && (
                    <div
                      style={{
                        color: 'green',
                        marginBottom: '5px',
                        fontSize: '14px',
                      }}
                    >
                      {'This is a valid embedded Vimeo video'}
                    </div>
                  )}
                </div>
              ) : null}
              {!(
                from === 'Service Request' &&
                user.roles[0].role.name === 'Home Owner'
              ) && (
                <>
                  <Input
                    value={formData.name}
                    onChange={(e) => updateFormData({ name: e.target.value })}
                    label={`${formData.type || 'Document'} Name`}
                    placeholder={`${formData.type || 'Document'} Name`}
                    block
                    required={false}
                  />
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      updateFormData({ description: e.target.value })
                    }
                    label="Description"
                    placeholder="Description"
                    block
                    required={false}
                  />
                </>
              )}
              {from === 'propertyForm' && (
                <CheckBox
                  checked={formData.docAvailability}
                  value={formData.docAvailability}
                  label={`Make it available at ${variables.suite} level `}
                  onChange={(e) =>
                    updateFormData({
                      docAvailability: !formData.docAvailability,
                    })
                  }
                />
              )}
              {!(from === 'Service Request' || from === 'Contact') && (
                <CheckBox
                  checked={formData.isImportantDocument}
                  value={formData.isImportantDocument}
                  label={`Important Document`}
                  onChange={(e) =>
                    updateFormData({
                      isImportantDocument: !formData.isImportantDocument,
                    })
                  }
                />
              )}
            </Col>
          </Row>
        </>
      }
      footer={undefined}
      strapline={undefined}
      onExit={undefined}
    />
  );
};

export default DocumentForm;
